import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/reducers';
import { convertCentsToDollars, roundToThreeDecimals, roundToTwoDecimals } from 'utils/amountUtils';
import {
  EInvoiceCatalogElementType,
  InvoiceDraftModalData,
  InvoiceGlobalTaxGroups,
  InvoiceValidationError
} from '../create-invoice/types';
import {
  AdditionalTax,
  Customer,
  DiscountSettings,
  IInvoiceServiceCharge,
  InvoiceByIdResponseV2,
  InvoiceResponse,
  InvoiceResponseV2,
  JobData
} from '../invoices.model';
import { DiscountFormData, LineItemAdditionalTax, initialDiscountFormData } from '../types';
import { initServiceCharge } from '../create-invoice/constants';
import {
  calculateInvoiceFinalAmount,
  calculateInvoiceSubtotalAndNewDiscount,
  calculateLineItemAdditionalTaxInCurrency,
  calculateServiceChargeBaseAmount,
  calculateSummatedLineItemLevelTaxesCurrency,
  isSameCatalogLineItem,
  prepareInvoiceAttachments,
  prepareInvoiceCustomerFromV2Response,
  prepareInvoiceDiscountFromV2Response,
  prepareInvoiceLineItemsFromV2Response,
  prepareInvoiceServiceChargeFromV2Response
} from '../utils';
import { AbstractITax, ITax } from 'containers/product-catalog/taxes/taxes.model';
import { GlobalServiceChargeTax } from 'containers/product-catalog/catalog.model';
import { FeatureToggleState } from 'containers/settings/constants';
import { PaymentMode, TNC } from 'containers/settings/settings.model';
import { InvoiceLevelPaymentModes } from '../InvoicesSlice';
import { PaymentCategoryTypes } from 'containers/settings/type';
import { EstimateStatus } from 'containers/estimates/constants';
import { RelatedEstimate } from 'containers/jobs/jobs.model';
import { TipTimingEnum } from '../constants';
import { ReminderConfig } from 'components/reminder-modal/types';
import { transformInvoiceApiRemindersToComponentFormat } from '../create-invoice/util';
import { UploadAttachmentFile } from 'types/baseTypes';

/**
 * Invoices
 *
 * - `Service Charge` : Can be a % or amount ($)
 * let subtotal = 100,  service charge = 10% with (tax 1 (10%), tax 2 (20%))
 * serviceChargeBaseAmount = 10 % of 100  or 10$
 * serviceChargeTaxesAmount = tax1% of serviceChargeBaseAmount + tax2% of serviceChargeBaseAmount
 * serviceChargeTotalAmount = serviceChargeBaseAmount + serviceChargeTaxesAmount
 * final amount = subtotal + serviceChargeTotalAmount
 *
 */

// TODO move exported interfaces outside

/* This is slice for editing invoices */
export interface InvoiceLineItemEditPage {
  catalogueLineItemId?: number;
  id: string; // We assign the new index id to every new line item
  itemDescription: string;
  unitCost: string;
  originalUnitCost?: string;
  markupPercentage?: string;
  quantity: string;
  total: number;
  additionalDetails: string;
  taxes?: LineItemAdditionalTax[];
  sku?: string;
}

export interface TaxesAndFeesPayloadStateEditPage {
  surchargeState: InvoiceSurchargeStateEditPage;
  discountSettings: DiscountSettings;
}

export interface InvoiceAttachmentEditPage {
  id: string;
  url: string;
  title: string;
  imagePreview: string;
  description: string;
  source?: string;
  urlLink: string;
}

export interface InvoiceSurchargeStateEditPage {
  enabled: boolean;
  basePoints: string;
  percentage: string;
  valueInCurrency: number;
  percentageWithBps: string;
  valueInCurrencyWithBps: number;
  config: FeatureToggleState;
}

export interface InvoiceOptionsEditPage {
  createdAt: string;
  dueDate: string;
  invoiceDate?: string | null;
}

export interface AggregatedLineItemLevelTaxResponse {
  id: number;
  catalogueTaxId: number;
  name: string;
  percentage: string;
  amount: string;
}

export interface AggregatedLineItemLevelTax {
  id: number;
  name: string;
  percentage: string;
  amount: string;
}

export interface InvoicesEditSliceInitPayload {
  invoiceCustomerEditPage: Customer;
  additionalTaxesEditPage: AdditionalTax[];
  serviceCharge: IInvoiceServiceCharge;
  additionalTaxEnabledEditPage: boolean;
  discountSettingsEditPage: DiscountSettings;
  invoiceLineItemsEditPage: InvoiceLineItemEditPage[];
  currentSurchargeEditPage: InvoiceSurchargeStateEditPage;
  lineItemsTotalEditPage: number;
  invoiceSubTotalEditPage: number;
  lineItemsStateCountEditPage: number;
  invoiceAttachmentIdCountEditPage: number;
  discountFormDataEditPage: DiscountFormData;
  commentEditPage: string;
  selectedInvoiceAttachmentsEditPage: InvoiceAttachmentEditPage[];
  tipEnabledEditPage: boolean;
  invoiceEditedResponse: InvoiceResponse;
  finalAmountEditPage: number; // final amount = subtotal + service fee + additional tax + sum of line item level taxes
  invoiceOptionsEditPage: InvoiceOptionsEditPage;
  invoiceNumber: string;
  isSignatureEnabled: boolean;
  isServiceFeeEnabled: boolean;
  jobData: JobData[];
  invoiceStatus?: string;
  paymentModesFromInvoice: PaymentMode[];
  estimateStatus: string;
  estimateNumber: string;
  estimateData: RelatedEstimate;
  invoiceId: string;
  paymentRemindersEditPage?: ReminderConfig[];
  tipTimingEditPage?: TipTimingEnum;
  paymentRemindersEnabledEditPage?: boolean;
  partialPaymentEnabledEditPage?: boolean;
  invoiceReferenceNumberEditPage?: string;
}

export interface InvoicesEditState {
  invoiceCustomerEditPage: Customer;
  initialInvoiceCustomer: Customer;
  showCustomerNotSelectedErrorEditPage: boolean;
  invoiceLineItemsEditPage: InvoiceLineItemEditPage[];
  initialInvoiceLineItemsEditPage: InvoiceLineItemEditPage[];
  lineItemsTotalEditPage: number;
  invoiceSubTotalEditPage: number;
  initialSubTotalEditPage: number;
  lineItemsStateCountEditPage: number;
  invoiceAttachmentIdCountEditPage: number;
  discountSettingsEditPage: DiscountSettings;
  discountFormDataEditPage: DiscountFormData;
  currentSurchargeEditPage: InvoiceSurchargeStateEditPage;
  initialSurchargeStateEditPage: InvoiceSurchargeStateEditPage;
  selectedInvoiceAttachmentsEditPage: InvoiceAttachmentEditPage[];
  commentEditPage: string;
  tipEnabledEditPage: boolean;
  initialTipEnabledEditPage: boolean;
  invoiceEditedResponse: InvoiceResponseV2;
  finalAmountEditPage: number; // final amount = subtotal + service fee + service charge + sum of line item level taxes
  invoiceOptionsEditPage: InvoiceOptionsEditPage;
  invoiceValidationErrorsEditPage: InvoiceValidationError[];
  isFromPreview: boolean;
  tnc: TNC;
  appliedLineItemLevelTaxes: AdditionalTax[];
  selectedServiceCharge: IInvoiceServiceCharge;
  initialServiceCharge: IInvoiceServiceCharge;
  aggregatedLineItemLevelTaxes: AggregatedLineItemLevelTax[];
  invoiceNumber: string;
  invoiceSliceInitializationInProgress: boolean;
  invoiceDraftModalData?: InvoiceDraftModalData;
  isBackFromCreateTaxRoute: boolean;
  globalLineItemTaxes: ITax[];
  globalServiceChargeTaxes: GlobalServiceChargeTax[];
  isSignatureEnabledEditPage: boolean;
  isServiceFeeEnabledEditPage: boolean;
  isServiceFeeEnabledEditPageInitial: boolean;
  invoiceFromJobDataEditPage: JobData[];
  isFromJobEditPage: boolean;
  invoiceFromJobNumberEditPage: string;
  invoicePaymentModesEdit: InvoiceLevelPaymentModes[];
  paymentModesFromInvoiceById: PaymentMode[];
  noPaymentMethodsAvailableEdit: boolean;
  estimateStatus: EstimateStatus;
  estimateNumber: string;
  estimateId: string;
  availablePaymentModesEdit: PaymentMode[];
  isInitialisingPaymentModesEdit: boolean;
  // New fields for partial payments and reminders
  partialPaymentEnabledEditPage: boolean;
  paymentRemindersEnabledEditPage: boolean;
  initialPaymentRemindersEnabledEditPage: boolean;
  paymentRemindersEditPage: ReminderConfig[];
  tipTimingEditPage: TipTimingEnum;
  invoiceReferenceNumberEditPage: string;
  isDiscountEnabledInitialEditPage: boolean;
}

const initialSurchargeState: InvoiceSurchargeStateEditPage = {
  enabled: false,
  basePoints: null,
  percentage: null,
  valueInCurrency: null,
  percentageWithBps: null,
  valueInCurrencyWithBps: null,
  config: null
};

const initialInvoiceOptionsData: InvoiceOptionsEditPage = {
  createdAt: null,
  dueDate: null,
  invoiceDate: null
};

const initialSelectedTNC: TNC = {
  tncId: 0,
  enabled: false,
  tnc: ''
};

const initialState = {
  invoiceCustomerEditPage: null,
  initialInvoiceCustomer: null,
  showCustomerNotSelectedErrorEditPage: false,
  invoiceLineItemsEditPage: [],
  initialInvoiceLineItemsEditPage: [],
  currentSurchargeEditPage: initialSurchargeState,
  initialSurchargeStateEditPage: initialSurchargeState,
  lineItemsTotalEditPage: 0,
  invoiceSubTotalEditPage: 0,
  initialSubTotalEditPage: 0,
  lineItemsStateCountEditPage: 0,
  invoiceAttachmentIdCountEditPage: 0,
  discountSettingsEditPage: { enabled: true },
  discountFormDataEditPage: initialDiscountFormData,
  commentEditPage: null,
  selectedInvoiceAttachmentsEditPage: [],
  tipEnabledEditPage: true,
  initialTipEnabledEditPage: true,
  invoiceEditedResponse: null,
  finalAmountEditPage: 0,
  invoiceOptionsEditPage: initialInvoiceOptionsData,
  invoiceValidationErrorsEditPage: [],
  isFromPreview: false,
  tnc: initialSelectedTNC,
  appliedLineItemLevelTaxes: [],
  aggregatedLineItemLevelTaxes: [],
  invoiceNumber: null,
  selectedServiceCharge: initServiceCharge,
  initialServiceCharge: initServiceCharge,
  invoiceSliceInitializationInProgress: true,
  invoiceDraftModalData: null,
  isBackFromCreateTaxRoute: false,
  globalLineItemTaxes: [],
  globalServiceChargeTaxes: [],
  isSignatureEnabledEditPage: false,
  isServiceFeeEnabledEditPage: false,
  isServiceFeeEnabledEditPageInitial: false,
  invoiceFromJobDataEditPage: [],
  isFromJobEditPage: false,
  invoiceFromJobNumberEditPage: '',
  invoicePaymentModesEdit: [],
  paymentModesFromInvoiceById: [],
  noPaymentMethodsAvailableEdit: false,
  estimateStatus: null,
  estimateNumber: '',
  estimateId: '',
  availablePaymentModesEdit: [],
  isInitialisingPaymentModesEdit: true,
  // New fields for partial payments and reminders
  partialPaymentEnabledEditPage: false,
  paymentRemindersEnabledEditPage: false,
  initialPaymentRemindersEnabledEditPage: false,
  paymentRemindersEditPage: [],
  tipTimingEditPage: TipTimingEnum.FINAL_PAYMENT_ONLY,
  invoiceReferenceNumberEditPage: ''
} as InvoicesEditState;

const getLineItemsTotal = (lineItems: InvoiceLineItemEditPage[]) => {
  const lineItemsTotalEditPage = lineItems?.reduce((accumulator, lineItem) => {
    return accumulator + lineItem.total;
  }, 0);
  return roundToTwoDecimals(lineItemsTotalEditPage);
};

const calculateSurchargeInCurrency = (surchargePercent: string, subTotal: number) => {
  if (subTotal === null) {
    return null;
  }
  const surchargeInCurrency = (+surchargePercent * subTotal) / 100;
  return roundToTwoDecimals(surchargeInCurrency);
};

const calculateSurchargeWithBpsPercentage = (surchargeInCurrencyWithBps: number, subTotal: number) => {
  if (surchargeInCurrencyWithBps === 0) {
    return '0';
  }
  if (subTotal === null) {
    return null;
  }
  const surchargeWithBpsPercent = (surchargeInCurrencyWithBps / subTotal) * 100;
  // TODO check rounding to three decimals
  return roundToThreeDecimals(surchargeWithBpsPercent).toString();
};

const calculateSurchargeInCurrencyWithBps = (surchargeInCurrency: number, basePointsInCents: string) => {
  if (surchargeInCurrency === 0 || surchargeInCurrency === null) {
    return 0;
  }
  return roundToTwoDecimals(surchargeInCurrency + convertCentsToDollars(basePointsInCents));
};

export const findAppliedUniqueTaxes = ({
  lineItems = [],
  serviceCharge = initServiceCharge
}: {
  lineItems: InvoiceLineItemEditPage[];
  serviceCharge?: IInvoiceServiceCharge;
}): AdditionalTax[] => {
  const appliedLineItemLevelTaxesIdSet = new Set<number>();
  const appliedLineItemLevelTaxes: AdditionalTax[] = [];

  if (lineItems.length > 0) {
    lineItems.forEach(lineItem => {
      const taxesInLineItem: LineItemAdditionalTax[] = lineItem?.taxes ?? [];
      if (taxesInLineItem.length > 0) {
        taxesInLineItem.forEach(taxInLineItem => {
          const isTaxAlreadyAdded = appliedLineItemLevelTaxesIdSet.has(taxInLineItem.id);
          if (!isTaxAlreadyAdded && taxInLineItem) {
            appliedLineItemLevelTaxesIdSet.add(taxInLineItem.id);
            const appliedLineItemLevelTax: AdditionalTax = {
              id: taxInLineItem.id,
              name: taxInLineItem.name,
              percentage: taxInLineItem.percentage
            };
            appliedLineItemLevelTaxes.push(appliedLineItemLevelTax);
          }
        });
      }
    });
  }
  if (serviceCharge?.taxes?.length > 0) {
    const serviceChargeTaxes = serviceCharge?.taxes;
    serviceChargeTaxes.forEach(serviceChargeTax => {
      const isTaxAlreadyAdded = appliedLineItemLevelTaxes.some(
        alreadyAppliedTax => alreadyAppliedTax.id === serviceChargeTax.id
      );
      if (!isTaxAlreadyAdded) {
        appliedLineItemLevelTaxes.push(serviceChargeTax as AdditionalTax);
      }
    });
  }

  return appliedLineItemLevelTaxes;
};

const hasServiceCharge = (serviceCharge: IInvoiceServiceCharge) => {
  return serviceCharge !== initServiceCharge && serviceCharge !== null;
};

const updateServiceChargeWithBaseAmount = ({
  serviceCharge,
  subtotal
}: {
  serviceCharge: IInvoiceServiceCharge;
  subtotal: number;
}): { updatedServiceCharge: IInvoiceServiceCharge; serviceChargeBaseAmount: number } => {
  const serviceChargeBaseAmount = calculateServiceChargeBaseAmount({
    subtotal: subtotal,
    serviceCharge: serviceCharge
  });
  const updatedServiceCharge: IInvoiceServiceCharge = {
    ...serviceCharge,
    totalAmount: serviceChargeBaseAmount.toString()
  };
  return { updatedServiceCharge, serviceChargeBaseAmount };
};

export const invoiceEditSlice = createSlice({
  name: 'invoicesEdit',
  initialState,
  reducers: {
    resetInvoiceEditSlice: () => {
      return initialState;
    },
    updateInvoiceInitializationInProgress: (state, { payload }: PayloadAction<boolean>) => {
      state.invoiceSliceInitializationInProgress = payload;
    },
    updateIsBackFromCreateTaxInEditSlice: (state, { payload }: PayloadAction<boolean>) => {
      state.isBackFromCreateTaxRoute = payload;
    },
    // This payload comes from the api call which fetches the invoice which is being edited
    initialiseInvoiceEditSlice: (state, { payload }: PayloadAction<InvoicesEditSliceInitPayload>) => {
      state.isFromJobEditPage = payload.jobData?.length > 0;
      state.invoiceFromJobNumberEditPage = payload.jobData[0]?.jobNumber;
      state.estimateNumber = payload.estimateNumber ?? '';
      state.estimateStatus = (payload.estimateStatus as EstimateStatus) ?? null;
      state.estimateId = payload.estimateData?.id ?? '';
      state.invoiceCustomerEditPage = payload.invoiceCustomerEditPage;
      state.initialInvoiceCustomer = payload.invoiceCustomerEditPage;

      state.lineItemsStateCountEditPage = payload.lineItemsStateCountEditPage;
      state.invoiceLineItemsEditPage = payload.invoiceLineItemsEditPage;
      state.initialInvoiceLineItemsEditPage = payload.invoiceLineItemsEditPage;

      // initialise applied item level taxes array
      state.appliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: payload?.invoiceLineItemsEditPage,
        serviceCharge: payload?.serviceCharge
      });

      const totalCostOfLineItems = getLineItemsTotal(payload.invoiceLineItemsEditPage);
      state.lineItemsTotalEditPage = totalCostOfLineItems;
      state.invoiceOptionsEditPage = payload.invoiceOptionsEditPage;

      const { subtotal: initialSubtotal } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: payload.discountFormDataEditPage
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormDataEditPage };

      state.initialSubTotalEditPage = initialSubtotal;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.discountSettingsEditPage.enabled
          ? payload.discountFormDataEditPage
          : initialDiscountFormData
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormDataEditPage };

      // Update subtotal state
      state.invoiceSubTotalEditPage = subtotal;

      // update discount data
      if (state.discountSettingsEditPage.enabled) {
        state.discountFormDataEditPage = newDiscountData;
      }

      state.isServiceFeeEnabledEditPageInitial = payload.isServiceFeeEnabled;
      if (!state.currentSurchargeEditPage.enabled) {
        state.isServiceFeeEnabledEditPage = false;
      } else if (state.currentSurchargeEditPage.config === FeatureToggleState.ALWAYS_ON) {
        state.isServiceFeeEnabledEditPage = true;
      } else state.isServiceFeeEnabledEditPage = payload.isServiceFeeEnabled;

      // Surcharge is not pre-filled during invoice edit
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabledEditPage) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.currentSurchargeEditPage.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurchargeEditPage.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurchargeEditPage = {
          percentage: state.currentSurchargeEditPage.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurchargeEditPage.enabled,
          basePoints: state.currentSurchargeEditPage.basePoints,
          config: state.currentSurchargeEditPage.config,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };

        state.initialSurchargeStateEditPage = state.currentSurchargeEditPage;
      }

      // initialize service charge
      const serviceChargeInPayload = payload.serviceCharge;

      let updatedServiceCharge = serviceChargeInPayload;
      let serviceChargeBaseAmount = 0;
      // calculate total service charge base amount
      if (hasServiceCharge(serviceChargeInPayload)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: serviceChargeInPayload,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
        state.initialServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: payload.invoiceLineItemsEditPage,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmountEditPage = calculateInvoiceFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmountEditPage;

      if (state.invoiceLineItemsEditPage.length === 0) {
        state.discountFormDataEditPage = initialDiscountFormData;
      }

      state.commentEditPage = payload.commentEditPage;
      state.invoiceNumber = payload.invoiceNumber;
      state.invoiceAttachmentIdCountEditPage = payload.invoiceAttachmentIdCountEditPage ?? 0;
      state.selectedInvoiceAttachmentsEditPage = payload.selectedInvoiceAttachmentsEditPage;
      state.tipEnabledEditPage = payload.tipEnabledEditPage;
      state.invoiceSliceInitializationInProgress = false;
      state.isSignatureEnabledEditPage = payload.isSignatureEnabled;
      state.invoiceFromJobDataEditPage = payload.jobData;
      state.paymentModesFromInvoiceById = payload.paymentModesFromInvoice ?? [];
    },

    // V2 API initialize reducer
    initialiseInvoiceEditSliceFromV2: (state, { payload }: PayloadAction<InvoiceByIdResponseV2>) => {
      const invoiceData = payload.invoice;
      const customerData = payload.customer; //latest customer data

      state.isFromJobEditPage = invoiceData.relatedJobs?.length > 0;
      state.invoiceFromJobNumberEditPage = invoiceData.relatedJobs?.[0]?.jobNumber || '';
      state.estimateNumber = invoiceData.relatedEstimates?.[0]?.estimateNumber ?? '';
      state.estimateStatus = (invoiceData.relatedEstimates?.[0]?.status as EstimateStatus) ?? null;
      state.estimateId = invoiceData.relatedEstimates?.[0]?.id ?? '';

      // Prepare customer data using V2 response transformer
      state.invoiceCustomerEditPage = prepareInvoiceCustomerFromV2Response(customerData, invoiceData.merchantInfo?.id);
      state.initialInvoiceCustomer = { ...state.invoiceCustomerEditPage } as Customer;

      state.lineItemsStateCountEditPage = invoiceData.lineItems?.length || 0;

      // Prepare line items using V2 response transformer
      state.invoiceLineItemsEditPage = prepareInvoiceLineItemsFromV2Response(invoiceData.lineItems);
      state.initialInvoiceLineItemsEditPage = prepareInvoiceLineItemsFromV2Response(invoiceData.lineItems);

      // Initialize applied item level taxes array
      const preparedServiceCharge = prepareInvoiceServiceChargeFromV2Response(
        invoiceData.serviceCharges?.[0]
      ) as IInvoiceServiceCharge;
      state.appliedLineItemLevelTaxes = findAppliedUniqueTaxes({
        lineItems: state.invoiceLineItemsEditPage,
        serviceCharge: preparedServiceCharge
      });

      const totalCostOfLineItems = invoiceData.totalLineItemAmount || getLineItemsTotal(state.invoiceLineItemsEditPage);
      state.lineItemsTotalEditPage = totalCostOfLineItems;

      // Prepare discount data using V2 response transformer
      const discountFormData = prepareInvoiceDiscountFromV2Response(invoiceData.discount);

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: discountFormData
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormDataEditPage };

      state.initialSubTotalEditPage = subtotal;
      state.isDiscountEnabledInitialEditPage = state.discountSettingsEditPage.enabled || invoiceData.discount.enabled;
      // const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
      //   totalCostOfItems: totalCostOfLineItems,
      //   discountData: state.discountSettingsEditPage.enabled ? discountFormData : initialDiscountFormData
      // }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormDataEditPage };

      // Update subtotal state
      state.invoiceSubTotalEditPage = subtotal;

      // Update discount data
      // if (state.discountSettingsEditPage.enabled) {
      state.discountFormDataEditPage = newDiscountData;
      // }

      state.isServiceFeeEnabledEditPageInitial = invoiceData.serviceFee.enabled;
      if (state.currentSurchargeEditPage.config === FeatureToggleState.ALWAYS_ON) {
        state.isServiceFeeEnabledEditPage = true;
      } else state.isServiceFeeEnabledEditPage = invoiceData.serviceFee.enabled;

      // Surcharge calculation
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabledEditPage) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          invoiceData.serviceFee.value.toString(),
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          invoiceData.serviceFee.basePoints.toString()
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurchargeEditPage = {
          percentage: invoiceData.serviceFee.value.toString(),
          valueInCurrency: surchargeInCurrency,
          enabled: invoiceData.serviceFee.enabled,
          basePoints: invoiceData.serviceFee.basePoints.toString(),
          config: state.currentSurchargeEditPage.config,
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps
        };

        state.initialSurchargeStateEditPage = state.currentSurchargeEditPage;
      }

      // Initialize service charge
      let updatedServiceCharge = preparedServiceCharge;
      let serviceChargeBaseAmount = 0;

      if (hasServiceCharge(preparedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: preparedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
        state.initialServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;

      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.invoiceLineItemsEditPage,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmountEditPage = calculateInvoiceFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmountEditPage;

      if (state.invoiceLineItemsEditPage.length === 0) {
        state.discountFormDataEditPage = initialDiscountFormData;
      }

      state.commentEditPage = invoiceData.note;
      state.invoiceNumber = invoiceData.invoiceNumber;
      state.invoiceAttachmentIdCountEditPage = invoiceData.attachments?.length || 0;
      state.selectedInvoiceAttachmentsEditPage = prepareInvoiceAttachments(invoiceData.attachments, 'INVOICE');
      state.tipEnabledEditPage = invoiceData.tip.enabled;
      state.initialTipEnabledEditPage = invoiceData.tip.enabled;
      state.tipTimingEditPage = (invoiceData.tip.type as TipTimingEnum) || TipTimingEnum.ALL_PAYMENTS;
      state.isSignatureEnabledEditPage = invoiceData.signatureEnabled;
      state.invoiceFromJobDataEditPage = invoiceData.relatedJobs || [];
      state.paymentModesFromInvoiceById = invoiceData.paymentMethodList || [];
      state.partialPaymentEnabledEditPage = invoiceData.partialPaymentEnabled;
      state.paymentRemindersEnabledEditPage = invoiceData.reminderEnabled;
      state.initialPaymentRemindersEnabledEditPage = invoiceData.reminderEnabled;
      state.paymentRemindersEditPage = transformInvoiceApiRemindersToComponentFormat(invoiceData.reminders);
      state.invoiceReferenceNumberEditPage = invoiceData.referenceId || '';

      // Set invoice options
      state.invoiceOptionsEditPage = {
        createdAt: invoiceData.createdAt,
        dueDate: invoiceData.invoiceDueDate,
        invoiceDate: invoiceData.invoiceDate
      };
      state.invoiceSliceInitializationInProgress = false;
    },

    updateInvoiceCustomerEditPage: (state, { payload }: PayloadAction<Customer>) => {
      state.invoiceCustomerEditPage = payload;
    },

    removeInvoiceCustomerEditPage: state => {
      state.invoiceCustomerEditPage = null;
    },

    // update amount states during these operations add, remove, edit, tax
    addInvoiceLineItemEditPage: (state, { payload }: PayloadAction<InvoiceLineItemEditPage>) => {
      const updatedLineItemsList = [
        ...state.invoiceLineItemsEditPage,
        {
          ...payload,
          id: state.lineItemsStateCountEditPage.toString(),
          total: roundToTwoDecimals(+payload.unitCost * +payload.quantity)
        }
      ];
      state.lineItemsStateCountEditPage = state.lineItemsStateCountEditPage + 1;
      state.invoiceLineItemsEditPage = updatedLineItemsList;

      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.lineItemsTotalEditPage = totalCostOfLineItems;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.discountFormDataEditPage
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormDataEditPage };

      // Update subtotal state
      state.invoiceSubTotalEditPage = subtotal;
      // update discount data
      state.discountFormDataEditPage = newDiscountData;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabledEditPage) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.currentSurchargeEditPage.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurchargeEditPage.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurchargeEditPage = {
          percentage: state.currentSurchargeEditPage.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurchargeEditPage.enabled,
          basePoints: state.currentSurchargeEditPage.basePoints,
          config: state.currentSurchargeEditPage.config,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentageWithBps: surchargeWithBpsPercentage
        };
      }
      // update applied line item taxes array
      const itemLevelTaxesInPayload = payload?.taxes ?? [];
      let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = state.appliedLineItemLevelTaxes;
      if (itemLevelTaxesInPayload.length > 0) {
        itemLevelTaxesInPayload.forEach(itemLevelTaxInPayload => {
          const isLineItemTaxAlreadySelected = modifiedAppliedLineItemLevelTaxes.find(
            tax => tax.id === itemLevelTaxInPayload.id
          );

          if (!isLineItemTaxAlreadySelected) {
            modifiedAppliedLineItemLevelTaxes = [
              ...modifiedAppliedLineItemLevelTaxes,
              itemLevelTaxInPayload as AdditionalTax
            ];
          }
        });
      }
      state.appliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmountEditPage = calculateInvoiceFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmountEditPage;

      if (state.invoiceLineItemsEditPage.length === 0) {
        state.discountFormDataEditPage = initialDiscountFormData;
      }
    },
    modifyLineItemEditPage: (state, { payload }: PayloadAction<InvoiceLineItemEditPage>) => {
      let lineItemToUpdate: InvoiceLineItemEditPage = null;
      const updatedLineItemsList = state.invoiceLineItemsEditPage.map(lineItem => {
        if (lineItem.id === payload.id) {
          lineItemToUpdate = lineItem;
          return {
            ...lineItem,
            itemDescription: payload.itemDescription,
            unitCost: payload.unitCost,
            originalUnitCost: payload.originalUnitCost,
            markupPercentage: payload.markupPercentage,
            quantity: payload.quantity,
            total: roundToTwoDecimals(+payload.unitCost * +payload.quantity),
            additionalDetails: payload.additionalDetails,
            ...(payload?.taxes ? { taxes: payload.taxes } : { taxes: [] })
          };
        }

        return lineItem;
      });
      state.invoiceLineItemsEditPage = updatedLineItemsList;

      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.lineItemsTotalEditPage = totalCostOfLineItems;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.discountFormDataEditPage
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormDataEditPage };

      // Update subtotal state
      state.invoiceSubTotalEditPage = subtotal;
      // update discount data
      state.discountFormDataEditPage = newDiscountData;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabledEditPage) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.currentSurchargeEditPage.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurchargeEditPage.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurchargeEditPage = {
          percentage: state.currentSurchargeEditPage.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurchargeEditPage.enabled,
          basePoints: state.currentSurchargeEditPage.basePoints,
          config: state.currentSurchargeEditPage.config,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentageWithBps: surchargeWithBpsPercentage
        };
      }

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // update applied line item taxes array - currently selected line item level taxes
      const itemLevelTaxesInPayload = payload?.taxes ?? [];
      const taxesInItemBeforeUpdate = lineItemToUpdate?.taxes ?? [];

      if (itemLevelTaxesInPayload.length > 0) {
        itemLevelTaxesInPayload.forEach(itemLevelTaxInPayload => {
          const isLineItemTaxAlreadySelected = state.appliedLineItemLevelTaxes.find(
            tax => tax.id === itemLevelTaxInPayload.id
          );
          if (!isLineItemTaxAlreadySelected) {
            state.appliedLineItemLevelTaxes = [
              ...state.appliedLineItemLevelTaxes,
              itemLevelTaxInPayload as AdditionalTax
            ];
          }
        });
      }

      // remove the current line item's item level tax from applied item level taxes array
      //  if this tax is not applied on any other line items

      if (taxesInItemBeforeUpdate.length > 0) {
        taxesInItemBeforeUpdate.forEach(taxInItemBeforeUpdate => {
          const isTaxAppliedOnOtherItem = updatedLineItemsList.find(lineItem => {
            return lineItem?.taxes?.find(lineItemTax => lineItemTax.id === taxInItemBeforeUpdate.id);
          });

          const isTaxAppliedOnServiceCharge = state.selectedServiceCharge?.taxes?.find(
            tax => tax.id === taxInItemBeforeUpdate.id
          );

          if (!isTaxAppliedOnOtherItem && !isTaxAppliedOnServiceCharge) {
            state.appliedLineItemLevelTaxes = state.appliedLineItemLevelTaxes.filter(
              tax => tax.id !== taxInItemBeforeUpdate.id
            );
          }
        });
      }

      // Update final amount

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;
      const finalAmountEditPage = calculateInvoiceFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmountEditPage;
    },

    incrementCatalogLineItemQtyInEditSlice: (state, { payload }: PayloadAction<InvoiceLineItemEditPage>) => {
      const updatedLineItemsList = state.invoiceLineItemsEditPage.map(lineItem => {
        if (isSameCatalogLineItem({ primaryItem: payload, secondaryItem: lineItem })) {
          const newQty = (+lineItem.quantity + 1).toString();
          const totalLineItemCost = +newQty * +lineItem.unitCost;
          let lineItemAdditionalTaxesWithAmount: LineItemAdditionalTax[] = null;
          const lineItemTaxes: LineItemAdditionalTax[] = lineItem?.taxes ?? [];
          if (lineItemTaxes.length > 0) {
            lineItemAdditionalTaxesWithAmount = lineItemTaxes.map(tax => {
              return {
                ...tax,
                amount: calculateLineItemAdditionalTaxInCurrency(tax.percentage, totalLineItemCost)
              };
            });
          }
          return {
            ...lineItem,
            quantity: newQty,
            total: totalLineItemCost,
            ...(lineItemAdditionalTaxesWithAmount ? { taxes: lineItemAdditionalTaxesWithAmount } : {})
          };
        }
        return lineItem;
      });

      state.invoiceLineItemsEditPage = updatedLineItemsList;

      const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
      state.lineItemsTotalEditPage = totalCostOfLineItems;

      const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
        totalCostOfItems: totalCostOfLineItems,
        discountData: state.discountFormDataEditPage
      }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormDataEditPage };

      // Update subtotal state
      state.invoiceSubTotalEditPage = subtotal;
      // update discount data
      state.discountFormDataEditPage = newDiscountData;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabledEditPage) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.currentSurchargeEditPage.percentage,
          subtotal
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurchargeEditPage.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurchargeEditPage = {
          percentage: state.currentSurchargeEditPage.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurchargeEditPage.enabled,
          basePoints: state.currentSurchargeEditPage.basePoints,
          config: state.currentSurchargeEditPage.config,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentageWithBps: surchargeWithBpsPercentage
        };
      }

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: updatedLineItemsList,
        selectedServiceCharge: updatedServiceCharge
      });

      const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;
      const finalAmountEditPage = calculateInvoiceFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmountEditPage;
    },

    removeInvoiceLineItemEditPage: (state, { payload }: PayloadAction<InvoiceLineItemEditPage>) => {
      const updatedLineItemsList = state.invoiceLineItemsEditPage.filter(lineItem => lineItem.id !== payload.id);

      if (updatedLineItemsList.length > 0) {
        state.invoiceLineItemsEditPage = updatedLineItemsList;
        const totalCostOfLineItems = getLineItemsTotal(updatedLineItemsList);
        state.lineItemsTotalEditPage = totalCostOfLineItems;

        const { subtotal, discountData: newDiscountData } = calculateInvoiceSubtotalAndNewDiscount({
          totalCostOfItems: totalCostOfLineItems,
          discountData: state.discountFormDataEditPage
        }) ?? { subtotal: totalCostOfLineItems, discountData: state.discountFormDataEditPage };

        // Update subtotal state
        state.invoiceSubTotalEditPage = subtotal;
        // update discount data
        state.discountFormDataEditPage = newDiscountData;

        // Update surcharge state
        let surchargeInCurrencyWithBps = 0;
        if (state.isServiceFeeEnabledEditPage) {
          const surchargeInCurrency: number = calculateSurchargeInCurrency(
            state.currentSurchargeEditPage.percentage,
            subtotal
          );
          surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
            surchargeInCurrency,
            state.currentSurchargeEditPage.basePoints
          );

          const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

          state.currentSurchargeEditPage = {
            percentage: state.currentSurchargeEditPage.percentage,
            valueInCurrency: surchargeInCurrency,
            enabled: state.currentSurchargeEditPage.enabled,
            basePoints: state.currentSurchargeEditPage.basePoints,
            config: state.currentSurchargeEditPage.config,
            valueInCurrencyWithBps: surchargeInCurrencyWithBps,
            percentageWithBps: surchargeWithBpsPercentage
          };
        }

        // calculate service charge base amount
        let updatedServiceCharge = state.selectedServiceCharge;
        let serviceChargeBaseAmount = 0;
        if (hasServiceCharge(updatedServiceCharge)) {
          const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
            updateServiceChargeWithBaseAmount({
              serviceCharge: updatedServiceCharge,
              subtotal: subtotal
            });
          serviceChargeBaseAmount = baseAmount;
          updatedServiceCharge = updatedServiceChargeWithBaseAmount;
          state.selectedServiceCharge = updatedServiceCharge;
        }

        // update applied line item taxes array
        const singleItemLevelTaxesToRemove: LineItemAdditionalTax[] = payload?.taxes ?? [];
        if (singleItemLevelTaxesToRemove.length > 0) {
          singleItemLevelTaxesToRemove.forEach(itemLevelTaxOnCurrentItem => {
            const isTaxToRemoveAppliedOnOtherItem = updatedLineItemsList.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax?.id === itemLevelTaxOnCurrentItem?.id);
            });

            const isTaxAppliedOnServiceCharge = state.selectedServiceCharge?.taxes?.find(
              tax => tax.id === itemLevelTaxOnCurrentItem.id
            );

            if (!isTaxToRemoveAppliedOnOtherItem && !isTaxAppliedOnServiceCharge) {
              state.appliedLineItemLevelTaxes = state.appliedLineItemLevelTaxes.filter(
                tax => tax.id !== itemLevelTaxOnCurrentItem.id
              );
            }
          });
        }

        // Update final amount
        const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;

        // update sum of line item level taxes
        const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
          lineItems: updatedLineItemsList,
          selectedServiceCharge: updatedServiceCharge
        });

        const finalAmountEditPage = calculateInvoiceFinalAmount({
          subtotal,
          totalServiceChargeInCurrency: serviceChargeBaseAmount,
          surcharge: surchargeInCurrencyBpsValue,
          summatedLineItemLevelTaxesCurrency
        });

        state.finalAmountEditPage = finalAmountEditPage;
      } else {
        state.invoiceLineItemsEditPage = [];
        state.lineItemsTotalEditPage = null;
        state.invoiceSubTotalEditPage = null;
        state.currentSurchargeEditPage = {
          ...state.currentSurchargeEditPage,
          percentageWithBps: null,
          valueInCurrency: null,
          valueInCurrencyWithBps: null
        };
        state.selectedServiceCharge = initServiceCharge;
        state.appliedLineItemLevelTaxes = [];
        state.discountFormDataEditPage = initialDiscountFormData;
        state.finalAmountEditPage = 0;
      }
    },
    updateSelectedDiscountFormDataEditPage: (state, { payload }: PayloadAction<DiscountFormData>) => {
      state.discountFormDataEditPage = payload;

      const subtotal = state.lineItemsTotalEditPage - +payload.discountInCurrency;

      // Update subtotal state
      state.invoiceSubTotalEditPage = subtotal;

      // Update surcharge state
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabledEditPage) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.currentSurchargeEditPage.percentage,
          subtotal
        );
        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurchargeEditPage.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurchargeEditPage = {
          percentage: state.currentSurchargeEditPage.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurchargeEditPage.enabled,
          basePoints: state.currentSurchargeEditPage.basePoints,
          config: state.currentSurchargeEditPage.config,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentageWithBps: surchargeWithBpsPercentage
        };
      }

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.invoiceLineItemsEditPage,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmountEditPage = calculateInvoiceFinalAmount({
        subtotal,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        surcharge: surchargeInCurrencyBpsValue,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmountEditPage;
    },

    removeSelectedDiscountFormDataEditPage: state => {
      state.discountFormDataEditPage = initialDiscountFormData;

      const subtotal = state.lineItemsTotalEditPage;

      // Update subtotal state
      state.invoiceSubTotalEditPage = subtotal;

      // Update surcharge state - re-calculate surcharge in currency
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabledEditPage) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.currentSurchargeEditPage.percentage,
          subtotal
        );
        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurchargeEditPage.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(surchargeInCurrencyWithBps, subtotal);

        state.currentSurchargeEditPage = {
          percentage: state.currentSurchargeEditPage.percentage,
          valueInCurrency: surchargeInCurrency,
          enabled: state.currentSurchargeEditPage.enabled,
          basePoints: state.currentSurchargeEditPage.basePoints,
          config: state.currentSurchargeEditPage.config,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentageWithBps: surchargeWithBpsPercentage
        };
      }

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: subtotal
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.invoiceLineItemsEditPage,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmountEditPage = calculateInvoiceFinalAmount({
        subtotal,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmountEditPage;
    },

    // This action is dispatched after the taxes and fees api call
    updateInvoiceFeeSettingsEditPage: (state, { payload }: PayloadAction<TaxesAndFeesPayloadStateEditPage>) => {
      if (!payload.surchargeState.enabled) {
        state.isServiceFeeEnabledEditPage = false;
      } else if (payload.surchargeState.config === FeatureToggleState.ALWAYS_ON) {
        state.isServiceFeeEnabledEditPage = true;
      }
      const surchargeInCurrency: number = calculateSurchargeInCurrency(
        payload.surchargeState.percentage,
        state.invoiceSubTotalEditPage
      );

      const surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
        surchargeInCurrency,
        state.currentSurchargeEditPage.basePoints
      );
      const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(
        surchargeInCurrencyWithBps,
        state.invoiceSubTotalEditPage
      );

      state.currentSurchargeEditPage = {
        percentage: payload.surchargeState.percentage,
        valueInCurrency: surchargeInCurrency,
        enabled: payload.surchargeState.enabled,
        basePoints: payload.surchargeState.basePoints,
        config: payload.surchargeState.config,
        valueInCurrencyWithBps: surchargeInCurrencyWithBps,
        percentageWithBps: surchargeWithBpsPercentage
      };

      state.discountSettingsEditPage = payload.discountSettings;

      // Update final amount
      const surchargeInCurrencyBpsValue = state.currentSurchargeEditPage.enabled ? surchargeInCurrencyWithBps : 0;

      // calculate service charge base amount
      let updatedServiceCharge = state.selectedServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: state.invoiceSubTotalEditPage
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.invoiceLineItemsEditPage,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateInvoiceFinalAmount({
        subtotal: state.invoiceSubTotalEditPage,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount ?? 0,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmount;
    },

    // TODO optimise code
    updateSelectedServiceChargeInEditSlice: (state, { payload }: PayloadAction<IInvoiceServiceCharge>) => {
      const currentServiceCharge: IInvoiceServiceCharge = { ...state.selectedServiceCharge };
      const newServiceCharge = payload;
      state.selectedServiceCharge = newServiceCharge;

      // re-calculate the final amount

      // calculate service charge base amount
      let updatedServiceCharge = newServiceCharge;
      let serviceChargeBaseAmount = 0;
      if (hasServiceCharge(updatedServiceCharge)) {
        const { updatedServiceCharge: updatedServiceChargeWithBaseAmount, serviceChargeBaseAmount: baseAmount } =
          updateServiceChargeWithBaseAmount({
            serviceCharge: updatedServiceCharge,
            subtotal: state.invoiceSubTotalEditPage
          });
        serviceChargeBaseAmount = baseAmount;
        updatedServiceCharge = updatedServiceChargeWithBaseAmount;
        state.selectedServiceCharge = updatedServiceCharge;
      }

      const taxesInNewServiceCharge: AbstractITax[] = newServiceCharge?.taxes ?? [];
      const taxesInCurrentServiceCharge: AbstractITax[] = currentServiceCharge?.taxes ?? [];

      //  add taxes which are in the new service charge if they aren't already present in any of the line items
      if (
        (currentServiceCharge === initServiceCharge ||
          currentServiceCharge === null ||
          taxesInCurrentServiceCharge.length === 0) &&
        taxesInNewServiceCharge.length > 0
      ) {
        taxesInNewServiceCharge.forEach(serviceChargeTax => {
          const isLineItemTaxAlreadySelected = state.appliedLineItemLevelTaxes.find(
            tax => tax.id === serviceChargeTax.id
          );
          if (!isLineItemTaxAlreadySelected) {
            state.appliedLineItemLevelTaxes = [...state.appliedLineItemLevelTaxes, serviceChargeTax as AdditionalTax];
          }
        });
      } else {
        if (taxesInCurrentServiceCharge.length > 0 && taxesInNewServiceCharge.length > 0) {
          // Remove the taxes which were added because of the current service charge
          let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = [...state.appliedLineItemLevelTaxes];
          taxesInCurrentServiceCharge.forEach(serviceChargeTax => {
            const isTaxAppliedOnAnyLineItem = state.invoiceLineItemsEditPage.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax.id === serviceChargeTax.id);
            });
            if (!isTaxAppliedOnAnyLineItem) {
              modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
                tax => tax.id !== serviceChargeTax.id
              );
            }
          });

          //  add the taxes which are in the new service charge
          taxesInNewServiceCharge.forEach(serviceChargeTax => {
            const isLineItemTaxAlreadySelected = modifiedAppliedLineItemLevelTaxes.find(
              tax => tax.id === serviceChargeTax.id
            );
            if (!isLineItemTaxAlreadySelected) {
              modifiedAppliedLineItemLevelTaxes = [
                ...modifiedAppliedLineItemLevelTaxes,
                serviceChargeTax as AdditionalTax
              ];
            }
          });
          state.appliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
        } else if (taxesInCurrentServiceCharge.length > 0 && taxesInNewServiceCharge.length === 0) {
          // Remove the taxes which were added because of the current service charge
          let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = [...state.appliedLineItemLevelTaxes];
          taxesInCurrentServiceCharge.forEach(serviceChargeTax => {
            const isTaxAppliedOnAnyLineItem = state.invoiceLineItemsEditPage.find(lineItem => {
              return lineItem?.taxes?.find(tax => tax.id === serviceChargeTax.id);
            });
            if (!isTaxAppliedOnAnyLineItem) {
              modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
                tax => tax.id !== serviceChargeTax.id
              );
            }
          });
          state.appliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
        }
      }

      const surchargeInCurrencyBpsValue = state.isServiceFeeEnabledEditPage
        ? state.currentSurchargeEditPage.valueInCurrencyWithBps
        : 0;

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.invoiceLineItemsEditPage,
        selectedServiceCharge: updatedServiceCharge
      });

      const finalAmount = calculateInvoiceFinalAmount({
        subtotal: state.invoiceSubTotalEditPage,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: serviceChargeBaseAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmount;
    },

    removeSelectedServiceChargeFromEditSlice: state => {
      const currentSelectedServiceCharge = { ...state.selectedServiceCharge };
      state.selectedServiceCharge = initServiceCharge;

      // re-calculate the final amount
      const surchargeInCurrencyBpsValue = state.isServiceFeeEnabledEditPage
        ? state.currentSurchargeEditPage.valueInCurrencyWithBps
        : 0;

      // update applied line item taxes array
      const serviceChargeTaxesToRemove: LineItemAdditionalTax[] = currentSelectedServiceCharge?.taxes ?? [];
      let modifiedAppliedLineItemLevelTaxes: AdditionalTax[] = [...state.appliedLineItemLevelTaxes];
      if (serviceChargeTaxesToRemove.length > 0) {
        serviceChargeTaxesToRemove.forEach(taxInCurrentServiceCharge => {
          const isTaxToRemoveAppliedOnAnyLineItem: InvoiceLineItemEditPage = state.invoiceLineItemsEditPage.find(
            lineItem => {
              return lineItem?.taxes?.find(tax => tax?.id === taxInCurrentServiceCharge?.id);
            }
          );
          if (!isTaxToRemoveAppliedOnAnyLineItem) {
            modifiedAppliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes.filter(
              tax => tax.id !== taxInCurrentServiceCharge.id
            );
          }
        });
        state.appliedLineItemLevelTaxes = modifiedAppliedLineItemLevelTaxes;
      }

      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.invoiceLineItemsEditPage
      });

      const finalAmount = calculateInvoiceFinalAmount({
        subtotal: state.invoiceSubTotalEditPage,
        surcharge: surchargeInCurrencyBpsValue,
        totalServiceChargeInCurrency: 0,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmount;
    },

    updateInvoiceNotesEditPage: (state, { payload }: PayloadAction<string>) => {
      state.commentEditPage = payload;
    },

    addInvoiceAttachmentEditPage: (state, { payload }: PayloadAction<InvoiceAttachmentEditPage>) => {
      const updatedAttachmentList = [
        ...state.selectedInvoiceAttachmentsEditPage,
        { ...payload, id: payload.id ? payload.id : state.invoiceAttachmentIdCountEditPage.toString() }
      ];
      if (!payload.id) state.invoiceAttachmentIdCountEditPage = state.invoiceAttachmentIdCountEditPage + 1;
      state.selectedInvoiceAttachmentsEditPage = updatedAttachmentList;
    },

    addMultipleInvoiceAttachmentsEditPage: (state, { payload }: PayloadAction<UploadAttachmentFile[]>) => {
      let nextId = state.invoiceAttachmentIdCountEditPage;
      const newAttachments: InvoiceAttachmentEditPage[] = payload.map(p => {
        const id = p?.id ? p.id?.toString() : (nextId++).toString();
        return {
          id,
          url: p.url,
          title: p.title,
          imagePreview: p.previewData || p.urlLink,
          description: p.description ?? '',
          urlLink: p.urlLink,
          source: p.source || 'INVOICE'
        };
      });
      state.invoiceAttachmentIdCountEditPage = nextId;
      state.selectedInvoiceAttachmentsEditPage = [...state.selectedInvoiceAttachmentsEditPage, ...newAttachments];
    },

    removeInvoiceAttachmentEditPage: (state, { payload }: PayloadAction<InvoiceAttachmentEditPage>) => {
      const updatedAttachmentList = state.selectedInvoiceAttachmentsEditPage.filter(attachment => {
        return attachment.url !== payload.url;
      });
      state.selectedInvoiceAttachmentsEditPage = updatedAttachmentList;
    },

    modifyInvoiceAttachmentEditPage: (state, { payload }: PayloadAction<InvoiceAttachmentEditPage>) => {
      const updatedAttachmentList = state.selectedInvoiceAttachmentsEditPage.map(attachment => {
        if (attachment.id === payload.id) {
          return {
            ...attachment,
            url: payload.url,
            title: payload.title,
            imagePreview: payload.imagePreview,
            description: payload.description
          };
        }
        return attachment;
      });
      state.selectedInvoiceAttachmentsEditPage = updatedAttachmentList;
    },

    resetInvoiceAttachementStateEditPage: state => {
      state.selectedInvoiceAttachmentsEditPage = [];
    },

    toggleTipEnabledEditPage: state => {
      state.tipEnabledEditPage = !state.tipEnabledEditPage;
    },

    updateTipEnabledEditPage: (state, { payload }: PayloadAction<boolean>) => {
      state.tipEnabledEditPage = payload;
    },

    updateInvoiceEditedResponse: (state, { payload }: PayloadAction<InvoiceResponseV2>) => {
      state.invoiceEditedResponse = payload;
    },

    addInvoiceValidationErrorEditPage: (state, { payload }: PayloadAction<InvoiceValidationError>) => {
      state.invoiceValidationErrorsEditPage = [...state.invoiceValidationErrorsEditPage, payload];
    },

    removeInvoiceValidationErrorEditPage: (state, { payload }: PayloadAction<InvoiceValidationError>) => {
      state.invoiceValidationErrorsEditPage = state.invoiceValidationErrorsEditPage.filter(
        error => error.type !== payload.type
      );
    },

    resetInvoiceValidationErrorsEditPage: state => {
      state.invoiceValidationErrorsEditPage = [];
    },

    enableCustomerNotSelectedErrorEditPage: state => {
      state.showCustomerNotSelectedErrorEditPage = true;
    },

    disableCustomerNotSelectedErrorEditPage: state => {
      state.showCustomerNotSelectedErrorEditPage = false;
    },
    toggleIsFromPreview: (state, { payload }: PayloadAction<boolean>) => {
      state.isFromPreview = payload;
    },
    updateSelectedTNC: (state, { payload }: PayloadAction<TNC>) => {
      state.tnc = payload;
    },

    updateInvoiceDraftDataInEditSlice: (state, { payload }: PayloadAction<InvoiceDraftModalData>) => {
      state.invoiceDraftModalData = payload;
    },
    resetInvoiceDraftDataInEditSlice: state => {
      state.invoiceDraftModalData = null;
    },

    addCreatedTaxInInvoiceDraftEditSlice: (state, { payload }: PayloadAction<AbstractITax>) => {
      if (state?.invoiceDraftModalData?.modalConfig) {
        const currentModalConfig = state?.invoiceDraftModalData?.modalConfig ?? {};
        const currentModalConfigData = currentModalConfig?.data ?? {};
        if (state.invoiceDraftModalData.invoiceCatalogElementType === EInvoiceCatalogElementType.ITEM) {
          const currentlySelectedTaxes = currentModalConfigData?.selectedTaxes ?? [];
          state.invoiceDraftModalData = {
            ...state.invoiceDraftModalData,
            modalConfig: {
              ...currentModalConfig,
              data: { ...currentModalConfigData, selectedTaxes: [...currentlySelectedTaxes, payload] }
            }
          };
        } else {
          // for service charge draft
          const currentlySelectedTaxes = currentModalConfigData?.taxes ?? [];
          state.invoiceDraftModalData = {
            ...state.invoiceDraftModalData,
            modalConfig: {
              ...currentModalConfig,
              data: { ...currentModalConfigData, taxes: [...currentlySelectedTaxes, payload] }
            }
          };
        }
      }
    },
    addGlobalTaxesInEditInvoiceSlice: (
      state,
      { payload = { globalLineItemTaxes: [], globalServiceChargeTaxes: [] } }: PayloadAction<InvoiceGlobalTaxGroups>
    ) => {
      state.globalLineItemTaxes = payload.globalLineItemTaxes;
      state.globalServiceChargeTaxes = payload.globalServiceChargeTaxes;
    },

    updateLineItemsOrderInEditInvoiceSlice: (state, { payload }) => {
      state.invoiceLineItemsEditPage = payload;
    },
    updateIsSignatureEnabledEditPage: (state, { payload }) => {
      state.isSignatureEnabledEditPage = payload;
    },
    updateIsServiceFeeEnabledEditPage: (state, { payload }) => {
      state.isServiceFeeEnabledEditPage = payload;
      let surchargeInCurrencyWithBps = 0;
      if (state.isServiceFeeEnabledEditPage) {
        const surchargeInCurrency: number = calculateSurchargeInCurrency(
          state.currentSurchargeEditPage.percentage,
          state.invoiceSubTotalEditPage
        );

        surchargeInCurrencyWithBps = calculateSurchargeInCurrencyWithBps(
          surchargeInCurrency,
          state.currentSurchargeEditPage.basePoints
        );

        const surchargeWithBpsPercentage = calculateSurchargeWithBpsPercentage(
          surchargeInCurrencyWithBps,
          state.invoiceSubTotalEditPage
        );

        state.currentSurchargeEditPage = {
          percentageWithBps: surchargeWithBpsPercentage,
          valueInCurrencyWithBps: surchargeInCurrencyWithBps,
          percentage: state.currentSurchargeEditPage.percentage,
          valueInCurrency: surchargeInCurrency,
          config: state.currentSurchargeEditPage.config,
          enabled: state.currentSurchargeEditPage.enabled,
          basePoints: state.currentSurchargeEditPage.basePoints
        };
      }
      // update sum of line item level taxes
      const summatedLineItemLevelTaxesCurrency: number = calculateSummatedLineItemLevelTaxesCurrency({
        lineItems: state.invoiceLineItemsEditPage,
        selectedServiceCharge: state.selectedServiceCharge
      });

      const finalAmount = calculateInvoiceFinalAmount({
        subtotal: state.invoiceSubTotalEditPage,
        surcharge: surchargeInCurrencyWithBps,
        totalServiceChargeInCurrency: +state.selectedServiceCharge.totalAmount,
        summatedLineItemLevelTaxesCurrency
      });
      state.finalAmountEditPage = finalAmount;
    },
    initialiseInvoicePaymentModesEdit: (
      state,
      { payload }: PayloadAction<{ paymentModes: PaymentMode[]; finalAmount }>
    ) => {
      const { paymentModes, finalAmount } = payload;
      state.noPaymentMethodsAvailableEdit = paymentModes.filter(item => item.enabled && item.adminEnabled).length === 0;
      state.availablePaymentModesEdit = paymentModes.filter(item => item.enabled && item.adminEnabled);
      state.invoicePaymentModesEdit = paymentModes
        .filter(
          paymentMode =>
            paymentMode.category.type === PaymentCategoryTypes.SELF_CHECKOUT &&
            paymentMode.adminEnabled &&
            (paymentMode.enabled || !paymentMode.teamLevelVisiblity)
        )
        .map(paymentMode => {
          return {
            ...paymentMode,
            isSelected: state.paymentModesFromInvoiceById
              ? state.paymentModesFromInvoiceById?.find(item => item.id === paymentMode.id)?.enabled || false
              : false,
            isValidForAmount: finalAmount >= paymentMode.minimumAmount && finalAmount <= paymentMode.maximumAmount
          };
        });
      state.isInitialisingPaymentModesEdit = false;
    },
    setIsInitialisingPaymentModesEdit: (state, { payload }: PayloadAction<boolean>) => {
      state.isInitialisingPaymentModesEdit = payload;
    },
    updateInvoicePaymentModesEdit: (state, { payload }: PayloadAction<InvoiceLevelPaymentModes[]>) => {
      state.invoicePaymentModesEdit = payload;
    },

    // New reducers for partial payments and reminders
    updateDueDateEditPage: (state, { payload }: PayloadAction<string | null>) => {
      state.invoiceOptionsEditPage.dueDate = payload;
    },

    updatePartialPaymentEnabledEditPage: (state, { payload }: PayloadAction<boolean>) => {
      state.partialPaymentEnabledEditPage = payload;
    },

    togglePartialPaymentEnabledEditPage: state => {
      state.partialPaymentEnabledEditPage = !state.partialPaymentEnabledEditPage;
    },

    updatePaymentRemindersEnabledEditPage: (state, { payload }: PayloadAction<boolean>) => {
      state.paymentRemindersEnabledEditPage = payload;
    },

    togglePaymentRemindersEnabledEditPage: state => {
      state.paymentRemindersEnabledEditPage = !state.paymentRemindersEnabledEditPage;
    },

    updatePaymentRemindersEditPage: (state, { payload }: PayloadAction<ReminderConfig[]>) => {
      state.paymentRemindersEditPage = payload;
    },

    updateTipTimingEditPage: (state, { payload }: PayloadAction<TipTimingEnum>) => {
      state.tipTimingEditPage = payload;
    },

    updateInvoiceReferenceNumberEditPage: (state, { payload }: PayloadAction<string>) => {
      state.invoiceReferenceNumberEditPage = payload;
    }
  }
});

export const {
  updateIsBackFromCreateTaxInEditSlice,
  initialiseInvoiceEditSlice,
  initialiseInvoiceEditSliceFromV2,
  addInvoiceAttachmentEditPage,
  addMultipleInvoiceAttachmentsEditPage,
  addInvoiceLineItemEditPage,
  modifyInvoiceAttachmentEditPage,
  resetInvoiceAttachementStateEditPage,
  modifyLineItemEditPage,
  incrementCatalogLineItemQtyInEditSlice,
  removeInvoiceAttachmentEditPage,
  removeInvoiceCustomerEditPage,
  removeInvoiceLineItemEditPage,
  removeSelectedDiscountFormDataEditPage,
  toggleTipEnabledEditPage,
  updateInvoiceEditedResponse,
  updateInvoiceCustomerEditPage,
  updateInvoiceNotesEditPage,
  updateSelectedDiscountFormDataEditPage,
  updateTipEnabledEditPage,
  updateInvoiceFeeSettingsEditPage,
  addInvoiceValidationErrorEditPage,
  removeInvoiceValidationErrorEditPage,
  resetInvoiceValidationErrorsEditPage,
  enableCustomerNotSelectedErrorEditPage,
  disableCustomerNotSelectedErrorEditPage,
  toggleIsFromPreview,
  updateSelectedTNC,
  updateSelectedServiceChargeInEditSlice,
  removeSelectedServiceChargeFromEditSlice,
  updateInvoiceInitializationInProgress,
  updateInvoiceDraftDataInEditSlice,
  resetInvoiceDraftDataInEditSlice,
  addCreatedTaxInInvoiceDraftEditSlice,
  addGlobalTaxesInEditInvoiceSlice,
  updateLineItemsOrderInEditInvoiceSlice,
  updateIsSignatureEnabledEditPage,
  updateIsServiceFeeEnabledEditPage,
  initialiseInvoicePaymentModesEdit,
  updateInvoicePaymentModesEdit,
  resetInvoiceEditSlice,
  // New actions for partial payments and reminders
  updateDueDateEditPage,
  updatePartialPaymentEnabledEditPage,
  togglePartialPaymentEnabledEditPage,
  updatePaymentRemindersEnabledEditPage,
  togglePaymentRemindersEnabledEditPage,
  updatePaymentRemindersEditPage,
  updateTipTimingEditPage,
  updateInvoiceReferenceNumberEditPage
} = invoiceEditSlice.actions;

export const selectInvoiceForEditPage = (state: RootState) => state.rootReducer.invoicesEdit;
export const selectInvoiceCustomerEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceCustomerEditPage;
export const selectCurrentSurchargeEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.currentSurchargeEditPage;
export const selectDiscountSettingsEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.discountSettingsEditPage;
export const selectInvoiceLineItemsEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceLineItemsEditPage;
export const selectDiscountFormDataEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.discountFormDataEditPage;
export const selectSelectedServiceChargeFromEditSlice = (state: RootState) =>
  state.rootReducer.invoicesEdit.selectedServiceCharge;
export const selectAppliedLineItemLevelTaxesEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.appliedLineItemLevelTaxes;
export const selecteLineItemsTotalEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.lineItemsTotalEditPage;
export const selectInvoiceAttachmentsEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.selectedInvoiceAttachmentsEditPage;
export const selectInvoiceNotesEditPage = (state: RootState) => state.rootReducer.invoicesEdit.commentEditPage;
export const selectInvoiceNumber = (state: RootState) => state.rootReducer.invoicesEdit.invoiceNumber;
export const selectTipEnabledEditPage = (state: RootState) => state.rootReducer.invoicesEdit.tipEnabledEditPage;
export const selectInitialTipEnabledEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.initialTipEnabledEditPage;
export const selectInvoiceEditPage = (state: RootState) => state.rootReducer.invoicesEdit;
export const selectInvoiceEditedResponse = (state: RootState) => state.rootReducer.invoicesEdit.invoiceEditedResponse;
export const selectInvoiceFinalAmountEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.finalAmountEditPage;
export const selectInvoiceSubtotalEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceSubTotalEditPage;
export const selectInvoiceOptionsEditPage = (state: RootState) => state.rootReducer.invoicesEdit.invoiceOptionsEditPage;
export const selectInvoiceValidationErrorsEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceValidationErrorsEditPage;
export const selectShowCustomerNotSelectedErrorEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.showCustomerNotSelectedErrorEditPage;
export const selectIsFromPreview = (state: RootState) => state.rootReducer.invoicesEdit.isFromPreview;
export const selectTNCEditPage = (state: RootState) => state.rootReducer.invoicesEdit.tnc;
export const selectInvoiceSliceInitializationInProgress = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceSliceInitializationInProgress;
export const selectInvoiceDraftDataFromInvoiceEditSlice = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceDraftModalData;
export const selectIsBackFromCreateTaxInEditSlice = (state: RootState) =>
  state.rootReducer.invoicesEdit.isBackFromCreateTaxRoute;
export const selectGlobalLineItemTaxesInInvoiceEditSlice = (state: RootState) =>
  state.rootReducer.invoicesEdit.globalLineItemTaxes;
export const selectGlobalServiceChargeTaxesInInvoiceEditSlice = (state: RootState) =>
  state.rootReducer.invoicesEdit.globalServiceChargeTaxes;
export const selectIsSignatureEnabledEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.isSignatureEnabledEditPage;
export const selectIsServiceFeeEnabledEditPage = (state: RootState) => {
  return state.rootReducer.invoicesEdit.isServiceFeeEnabledEditPage;
};
export const selectJobDataForInvoiceEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceFromJobDataEditPage;
export const selectIsInvoiceFromJobEditPage = (state: RootState) => state.rootReducer.invoicesEdit.isFromJobEditPage;
export const selectJobNumberForInvoiceEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceFromJobNumberEditPage;
export const selectInvoicePaymentModesEdit = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoicePaymentModesEdit;
export const selectNoPaymentMethodsAvailableEdit = (state: RootState) =>
  state.rootReducer.invoicesEdit.noPaymentMethodsAvailableEdit;
export const selectEstimateNumberForInvoiceEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.estimateNumber;
export const selectEstimateStatusForInvoiceEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.estimateStatus;
export const selectEstimateIdForInvoiceEditPage = (state: RootState) => state.rootReducer.invoicesEdit.estimateId;
export const selectAvailablePaymentModesEdit = (state: RootState) =>
  state.rootReducer.invoicesEdit.availablePaymentModesEdit;
export const selectIsInitialisingPaymentModesEdit = (state: RootState) =>
  state.rootReducer.invoicesEdit.isInitialisingPaymentModesEdit;
// New selectors for partial payments and reminders
export const selectDueDateEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceOptionsEditPage?.dueDate;
export const selectPartialPaymentEnabledEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.partialPaymentEnabledEditPage;
export const selectPaymentRemindersEnabledEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.paymentRemindersEnabledEditPage;
export const selectInitialPaymentRemindersEnabledEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.initialPaymentRemindersEnabledEditPage;
export const selectPaymentRemindersEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.paymentRemindersEditPage;
export const selectTipTimingEditPage = (state: RootState) => state.rootReducer.invoicesEdit.tipTimingEditPage;
export const selectInvoiceReferenceNumberEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.invoiceReferenceNumberEditPage;
export const selectIsDiscountEnabledInitialEditPage = (state: RootState) =>
  state.rootReducer.invoicesEdit.isDiscountEnabledInitialEditPage;

export default invoiceEditSlice.reducer;
